import { useFormikContext } from 'formik';
import * as React from 'react';
import { useDebounce } from '../../hooks';

export interface IFormikObserverProps<T> {
  submitOnChange?: boolean;
  onChange?: (values: T) => void;
  onChangeAndDirty?: (values: T) => void;
  onDirtyChange?: (dirty: boolean) => void;
  debounceMs?: number;
}

/**
 * Listens for a change in values within a Formik form and runs specific callbacks.
 * All changes are debounced by default, although the default debounce is 0ms.
 */
export function FormikObserver<T>({
  debounceMs = 0,
  onChange,
  onDirtyChange,
  onChangeAndDirty,
  submitOnChange,
}: IFormikObserverProps<T>) {
  const { submitForm, values, dirty } = useFormikContext();
  const [debouncedValues, setDebouncedValues] = React.useState<any>(values);
  const internalValues = useDebounce(debouncedValues, debounceMs);

  React.useEffect(() => {
    setDebouncedValues(values);
  }, [values]);

  React.useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
    if (onChangeAndDirty && dirty) onChangeAndDirty(values as T);
  }, [values]);

  React.useEffect(() => {
    if (onChange) onChange(values as T);
    if (submitOnChange && dirty) submitForm();
  }, [internalValues]);

  return null;
}
